import { FormattingContext, FormattingOverrides, SYSTEM_FALLBACK } from './formatting-context';

/** A date value accepted by all formatting functions. */
export type DateInput = Date | string | number;

interface ZonedDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

type ZonedDateTimePartType = keyof ZonedDateTimeParts;

const WALL_CLOCK_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const ISO_INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;
const TIME_ZONE_OFFSET_PROBE_DISTANCE = 2 * 24 * 60 * 60 * 1000;
const INVALID_TIME_VALUE = 'Invalid time value';

export function localTimeZone(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || SYSTEM_FALLBACK.timeZone;
  } catch {
    return SYSTEM_FALLBACK.timeZone;
  }
}

/**
 * Resolves an effective context by applying optional per-call overrides.
 */
function applyOverrides(context: FormattingContext, overrides?: FormattingOverrides): FormattingContext {
  if (!overrides) {
    return context;
  }
  return {
    locale: overrides.locale ?? context.locale,
    currency: overrides.currency ?? context.currency,
    timeZone: overrides.timeZone ?? context.timeZone,
  };
}

/**
 * Formats a Date (or ISO string or timestamp) as a locale-aware date string.
 * Example: 2026-02-19 → "19.02.2026" (de-CH)
 */
export function formatDate(value: DateInput, context: FormattingContext, overrides?: FormattingOverrides): string {
  const { locale, timeZone } = applyOverrides(context, overrides);
  const date = toDate(value);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(date);
}

/**
 * Formats a Date (or ISO string or timestamp) as a locale-aware time string.
 * Example: 2026-02-19T14:30:00Z → "14:30" (de-CH, Europe/Zurich)
 */
export function formatTime(value: DateInput, context: FormattingContext, overrides?: FormattingOverrides): string {
  const { locale, timeZone } = applyOverrides(context, overrides);
  const date = toDate(value);
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(date);
}

/**
 * Formats a Date (or ISO string or timestamp) as a locale-aware date-time string.
 */
export function formatDateTime(value: DateInput, context: FormattingContext, overrides?: FormattingOverrides): string {
  const { locale, timeZone } = applyOverrides(context, overrides);
  const date = toDate(value);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(date);
}

/**
 * Formats a numeric value as a locale-aware currency string.
 * Example: 1234.5 → "CHF 1'234.50" (de-CH, CHF)
 */
export function formatCurrency(value: number, context: FormattingContext, overrides?: FormattingOverrides): string {
  const { locale, currency } = applyOverrides(context, overrides);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Formats a numeric value as a locale-aware number string.
 * Example: 1234567.89 → "1'234'567.89" (de-CH)
 */
export function formatNumber(
  value: number,
  context: FormattingContext,
  overrides?: FormattingOverrides & { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  const { locale } = applyOverrides(context, overrides);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: overrides?.minimumFractionDigits,
    maximumFractionDigits: overrides?.maximumFractionDigits,
  }).format(value);
}

/**
 * Formats a numeric value (0–1) as a locale-aware percent string.
 * Example: 0.856 → "85.6%" (en-CH) or "85,6 %" (de-CH)
 *
 * @param value - A decimal value between 0 and 1, e.g. 0.856 for 85.6%
 */
export function formatPercent(
  value: number,
  context: FormattingContext,
  overrides?: FormattingOverrides & { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  const { locale } = applyOverrides(context, overrides);
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: overrides?.minimumFractionDigits ?? 0,
    maximumFractionDigits: overrides?.maximumFractionDigits ?? 2,
  }).format(value);
}

/**
 * Formats a date range (start/end) as a compact locale-aware string.
 * Example: "19.02.2026 – 28.02.2026" (de-CH)
 */
export function formatDateRange(start: DateInput, end: DateInput, context: FormattingContext, overrides?: FormattingOverrides): string {
  const { locale, timeZone } = applyOverrides(context, overrides);
  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  });
  return `${formatter.format(toDate(start))} – ${formatter.format(toDate(end))}`;
}

/**
 * Formats a time range (start/end) as a compact locale-aware string.
 * Example: "08:00 – 17:00" (de-CH)
 */
export function formatTimeRange(start: DateInput, end: DateInput, context: FormattingContext, overrides?: FormattingOverrides): string {
  const { locale, timeZone } = applyOverrides(context, overrides);
  const formatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
  return `${formatter.format(toDate(start))} – ${formatter.format(toDate(end))}`;
}

/**
 * General-purpose locale-aware date formatting with custom Intl.DateTimeFormatOptions.
 *
 * Use this for format patterns not covered by the dedicated formatDate/formatTime/etc. functions
 * (e.g. month-only, weekday-only, or month+year display strings).
 *
 * @example
 * // "February 2026" (en-CH)
 * formatCustom(date, context, { month: 'long', year: 'numeric' })
 */
export function formatCustom(
  value: DateInput,
  context: FormattingContext,
  options: Intl.DateTimeFormatOptions,
  overrides?: FormattingOverrides
): string {
  const { locale, timeZone } = applyOverrides(context, overrides);
  return new Intl.DateTimeFormat(locale, { timeZone, ...options }).format(toDate(value));
}

export function formatTimeInputValue(value: DateInput, context: FormattingContext, overrides?: FormattingOverrides): string {
  const parts = getInstantZonedDateTimeParts(value, context, overrides);
  if (!parts) {
    return '';
  }

  const { hour, minute } = parts;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function localHourFraction(value: DateInput, context: FormattingContext, overrides?: FormattingOverrides): number | null {
  const parts = getInstantZonedDateTimeParts(value, context, overrides);
  if (!parts) {
    return null;
  }

  const { hour, minute } = parts;
  return hour + minute / 60;
}

export function wallClockToUtcIso(dateKey: string, time: string, context: FormattingContext, overrides?: FormattingOverrides): string {
  const { timeZone } = applyOverrides(context, overrides);
  const wallClock = parseWallClock(dateKey, time);
  const wallClockTimestamp = wallClock.getTime();
  const matchingTimestamps = getTimeZoneOffsets(wallClockTimestamp, timeZone)
    .map(offset => wallClockTimestamp - offset)
    .filter(timestamp => toUtcTimestamp(getZonedDateTimeParts(new Date(timestamp), timeZone)) === wallClockTimestamp);

  if (matchingTimestamps.length === 0) {
    throw new RangeError(INVALID_TIME_VALUE);
  }

  return new Date(Math.max(...matchingTimestamps)).toISOString();
}

/**
 * Formats a Date as a local-time ISO date string (YYYY-MM-DD).
 *
 * Unlike `date.toISOString().split('T')[0]`, this uses the **local** calendar
 * date (getFullYear/getMonth/getDate) so the result is never shifted by the
 * timezone offset.
 *
 * Use this whenever you need a date key that represents "today" or dates
 * derived from local-time operations like `setDate()` / `setHours(0,0,0,0)`.
 */
/** @deprecated Use `toLocalDateKey` from `@zag/calendar/models`. */
export function toLocalIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Coerces a date-like value to a Date instance.
 */
function toDate(value: DateInput): Date {
  if (value instanceof Date) {
    return value;
  }
  return new Date(value);
}

function getInstantZonedDateTimeParts(
  value: DateInput,
  context: FormattingContext,
  overrides?: FormattingOverrides
): ZonedDateTimeParts | null {
  const date = toInstantDate(value);
  if (!date) {
    return null;
  }

  const { timeZone } = applyOverrides(context, overrides);
  return getZonedDateTimeParts(date, timeZone);
}

function toInstantDate(value: DateInput): Date | null {
  if (typeof value === 'string' && !isValidIsoInstant(value)) {
    return null;
  }

  const date = toDate(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isValidIsoInstant(value: string): boolean {
  if (!ISO_INSTANT_PATTERN.test(value)) {
    return false;
  }

  const wallClock = new Date(`${value.slice(0, 16)}:00.000Z`);
  return !Number.isNaN(wallClock.getTime()) && wallClock.toISOString().slice(0, 16) === value.slice(0, 16);
}

function getZonedDateTimeParts(date: Date, timeZone: string): ZonedDateTimeParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  return {
    year: getNumericPart(parts, 'year'),
    month: getNumericPart(parts, 'month'),
    day: getNumericPart(parts, 'day'),
    hour: getNumericPart(parts, 'hour'),
    minute: getNumericPart(parts, 'minute'),
  };
}

function getNumericPart(parts: Intl.DateTimeFormatPart[], type: ZonedDateTimePartType): number {
  const value = parts.find(part => part.type === type)?.value;
  if (value === undefined) {
    throw new RangeError(INVALID_TIME_VALUE);
  }
  return Number(value);
}

function getTimeZoneOffsets(wallClockTimestamp: number, timeZone: string): number[] {
  return [-TIME_ZONE_OFFSET_PROBE_DISTANCE, 0, TIME_ZONE_OFFSET_PROBE_DISTANCE].map(distance => {
    const instantTimestamp = wallClockTimestamp + distance;
    return toUtcTimestamp(getZonedDateTimeParts(new Date(instantTimestamp), timeZone)) - instantTimestamp;
  });
}

function parseWallClock(dateKey: string, time: string): Date {
  const value = `${dateKey}T${time}`;
  if (!WALL_CLOCK_PATTERN.test(value)) {
    throw new RangeError(INVALID_TIME_VALUE);
  }

  const date = new Date(`${value}:00.000Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 16) !== value) {
    throw new RangeError(INVALID_TIME_VALUE);
  }
  return date;
}

function toUtcTimestamp(parts: ZonedDateTimeParts): number {
  const date = new Date(0);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  date.setUTCHours(parts.hour, parts.minute, 0, 0);
  return date.getTime();
}
